/**
 * <waitlist-modal> — "notify me when it's back" for a product variant.
 *
 * Signing up is the newsletter flow with a different second step: Shopify's
 * customer form (consent + tags), the Klaviyo list, then the product appended
 * to the profile's `waitlist_products` list property — where the sign-up modal
 * sets `scent_preference`. No persistent state beyond a one-shot
 * sessionStorage note for the /challenge round-trip.
 *
 * Opens from any <a href="…#waitlist">. The product form ships its link as an
 * inert <template data-waitlist-trigger>; this element stamps it into the page
 * on connect, so the link only exists when the modal does.
 *
 * What gets recorded, in order:
 *   1. The trigger's own data-variant-id (+ data-product-title, data-product-url).
 *   2. On a product page, the selected variant: option inputs → ?variant= →
 *      [name="id"] → the section's default.
 *   3. Nothing resolvable → the click is left alone, so a link to
 *      "/products/x#waitlist" simply navigates and opens there.
 *
 * Requires signup-client.js (window.SignupClient) to be loaded first.
 */
(() => {
  if (customElements.get('waitlist-modal')) return;

  const CLOSE_MS = 450; // fallback only; the real value is read from .closing's transition-duration
  const PENDING_KEY = 'waitlist:pending'; // sessionStorage, only across the /challenge round-trip

  let primary = null;

  const session = {
    get() { try { return JSON.parse(sessionStorage.getItem(PENDING_KEY)); } catch { return null; } },
    set(value) { try { sessionStorage.setItem(PENDING_KEY, JSON.stringify(value)); } catch { /* ignore */ } },
    clear() { try { sessionStorage.removeItem(PENDING_KEY); } catch { /* ignore */ } },
  };

  class WaitlistModal extends HTMLElement {
    connectedCallback() {
      if (primary && primary !== this && primary.isConnected) {
        this.hidden = true; // duplicate (cloned section etc.): stay inert
        return;
      }
      primary = this;

      this.config = JSON.parse(this.querySelector('[data-config]').textContent);
      this.product = this.config.product?.id ? this.config.product : null; // null off product pages
      this.dialog = this.querySelector('dialog');
      this.panels = [...this.querySelectorAll('section[data-step]')];
      this.form = this.querySelector('form');
      this.hash = `#${this.dataset.trigger}`;
      this.subject = null; // { variantId, label, url } for the open dialog

      this.dialog.addEventListener('click', this.onAction);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.form.addEventListener('submit', this.onSubmit);
      document.addEventListener('click', this.onTrigger);
      document.addEventListener('shopify:section:select', this.onEditorSelect);
      document.addEventListener('shopify:section:deselect', this.onEditorDeselect);

      this.stampTriggers();
      this.boot();
    }

    disconnectedCallback() {
      if (primary === this) primary = null;
      document.removeEventListener('click', this.onTrigger);
      document.removeEventListener('shopify:section:select', this.onEditorSelect);
      document.removeEventListener('shopify:section:deselect', this.onEditorDeselect);
    }

    /* ── triggers ───────────────────────────────────────────────────────── */

    /** Triggers ship as inert <template>s; make them real. Idempotent. */
    stampTriggers() {
      for (const tpl of document.querySelectorAll('template[data-waitlist-trigger]')) {
        tpl.replaceWith(tpl.content.cloneNode(true));
      }
    }

    /* ── lifecycle ──────────────────────────────────────────────────────── */

    boot() {
      if (location.pathname === '/challenge') return;

      // Back from a native submit (the /challenge round-trip): finish on the done panel.
      const pending = session.get();
      if (pending) {
        session.clear();
        if (SignupClient.outcomeFromUrl(location.href)) {
          this.subject = pending;
          this.render('done');
          this.dialog.showModal();
          return;
        }
      }

      if (location.hash === this.hash) this.open();
    }

    render(step) {
      let active = null;
      for (const panel of this.panels) {
        const on = panel.dataset.step === step;
        panel.hidden = !on;
        if (on) active = panel;
      }
      this.dialog.dataset.activeStep = step;
      this.dialog.setAttribute('aria-labelledby', active.querySelector('[data-title]').id);

      const label = this.subject?.label || '';
      for (const el of this.querySelectorAll('[data-variant-title]')) {
        el.textContent = label;
        el.hidden = !label;
      }
      return active;
    }

    open(subject = this.resolveSubject(null)) {
      if (!subject) return; // nothing on this page to subscribe to
      this.subject = subject;
      const panel = this.render('form');
      if (!this.dialog.open) this.dialog.showModal();
      panel.focus({ preventScroll: true });
    }

    close() {
      if (!this.dialog.open) return;
      this.dialog.classList.add('closing');
      const ms = parseFloat(getComputedStyle(this.dialog).transitionDuration) * 1000 || CLOSE_MS;
      setTimeout(() => {
        this.dialog.close();
        this.dialog.classList.remove('closing');
        this.form.reset();
        this.setError(null);
      }, ms);
      if (location.hash === this.hash) {
        history.replaceState(null, '', location.pathname + location.search);
      }
    }

    setBusy(on) {
      this.form.querySelector('[type="submit"]').disabled = on;
      this.form.setAttribute('aria-busy', String(on));
    }

    setError(message) {
      const el = this.querySelector('[data-error]');
      el.hidden = !message;
      el.textContent = message || '';
    }

    /* ── subject (what to subscribe to) ─────────────────────────────────── */

    resolveSubject(link) {
      if (link?.dataset.variantId) {
        return {
          variantId: link.dataset.variantId,
          label: link.dataset.productTitle || '',
          url: link.dataset.productUrl || '',
        };
      }
      if (this.product) {
        const variantId = this.resolveVariantId(link);
        return { variantId, label: this.variantLabel(variantId), url: this.product.url };
      }
      return null;
    }

    resolveVariantId(link) {
      const { variants, defaultVariant } = this.config;
      const known = (id) => (id && variants[String(id)] ? String(id) : null);

      // Selected option inputs → variant whose options match.
      const form = document.querySelector('form[action*="/cart/add"]');
      if (form) {
        const chosen = [...form.querySelectorAll('[name^="options["]')]
          .filter((el) => el.tagName === 'SELECT' || el.checked)
          .map((el) => el.value);
        if (chosen.length) {
          const match = Object.entries(variants).find(
            ([, v]) => v.options.length === chosen.length && v.options.every((o, i) => o === chosen[i]),
          );
          if (match) return match[0];
        }
      }

      return (
        known(new URLSearchParams(location.search).get('variant')) ||
        known(form?.querySelector('[name="id"]')?.value) ||
        known(link?.dataset.variantId) ||
        String(defaultVariant)
      );
    }

    variantLabel(variantId) {
      const v = this.config.variants[variantId];
      if (!v || this.product.hasOnlyDefaultVariant) return this.product.title;
      return `${this.product.title} — ${v.title}`;
    }

    /* ── events ─────────────────────────────────────────────────────────── */

    onTrigger = (event) => {
      const link = event.target.closest(`a[href$="${this.hash}"]`);
      if (!link) return;
      const subject = this.resolveSubject(link);
      if (!subject) return; // let it navigate (e.g. to the product page, which opens the modal there)
      event.preventDefault();
      this.open(subject);
    };

    onAction = (event) => {
      const el = event.target.closest('[data-action]');
      if (el?.dataset.action === 'close') this.close();
    };

    onCancel = (event) => {
      event.preventDefault();
      this.close();
    };

    onSubmit = async (event) => {
      event.preventDefault();
      const input = this.form.elements['contact[email]'];
      if (!input.checkValidity()) {
        input.reportValidity();
        return;
      }
      const email = input.value.trim();
      const { companyId, listId } = this.config.klaviyo || {};
      const { label } = this.subject;

      this.setBusy(true);
      this.setError(null);

      // Klaviyo: best-effort and independent of Shopify's answer, same as the
      // newsletter. List first (consent), then the product onto the profile —
      // appended, so joining a second waitlist adds rather than overwrites.
      if (companyId && listId) {
        SignupClient.subscribeToList({ companyId, listId, email, source: `Waitlist: ${label}` })
          .then(() => SignupClient.setProfileProperties({
            companyId,
            email,
            append: { waitlist_products: label },
          }))
          .catch((error) => console.warn('[waitlist-modal] klaviyo', error));
      }

      try {
        const outcome = await SignupClient.postCustomerForm(this.form);

        // Bot protection: hand the round-trip to the browser; boot() finishes on return.
        if (outcome === 'challenge') {
          session.set(this.subject);
          this.form.submit();
          return;
        }

        this.form.reset();
        this.render('done').focus({ preventScroll: true });
      } catch (error) {
        console.error('[waitlist-modal]', error);
        this.setError(this.config.errorText);
      } finally {
        this.setBusy(false);
      }
    };

    onEditorSelect = (event) => { if (event.target.contains(this)) this.open(); };
    onEditorDeselect = (event) => { if (event.target.contains(this)) this.close(); };
  }

  customElements.define('waitlist-modal', WaitlistModal);
})();